import * as React from "react"
import {Fragment, useContext, useState} from 'react'
import {Dialog, Disclosure, Menu, Popover, Transition} from '@headlessui/react'
import {graphql, Link, navigate, useStaticQuery} from "gatsby";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import LanguageContext from "../context/LanguageContext";
import {joinClassNames} from "../utils/utils";
import logo from "../images/leaderfy_logo_light.svg";
import { useLocation } from '@reach/router';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const {language} = useContext(LanguageContext);
    const location = useLocation();

    const data = useStaticQuery(graphql`
         {
            strapi {
                navigations (
                    filters: { 
                        Name: { eq: "Top Navigation" },
                        locale: { in: ["de", "en"] }
                    } 
                ) {
                    documentId
                    Name
                    locale
                    Items {
                      Name
                      Link {
                        Name
                        Page {
                          Slug
                        }
                      }
                      Children {
                        Name
                        Link {
                          Name
                          Page {
                            Slug
                          }
                        }
                      }
                    }
                }
            }
        }
    `)

    const navigation = data.strapi.navigations.find(
      (nav) => nav.locale === language
    );

    if (!navigation) {
        return null;
    }

    const LanguageSwitcher = () => {
        const { language, setLanguage, availableLanguages } = useContext(LanguageContext);

        const languageLabels = {
            de: 'Deutsch',
            en: 'English'
        };

        const changeLanguage = (newLanguageCode) => {
            const path = window.location.pathname;

            const newPath = path.length <= 3 ? `/${newLanguageCode}/` : path.replace(/^\/([a-z]{2}(?:-[A-Z]{2})?)\//, `/${newLanguageCode}/`);

            navigate(newPath, { replace: true });

            setLanguage(newLanguageCode);
        };

        const currentLanguageLabel = languageLabels[language] || language.toUpperCase();

        return (
          <Menu as="div" className="relative inline-block text-left">
              <div>
                  <Menu.Button
                    className="inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                      <svg aria-hidden="true" className="w-5 h-5 mr-1 " width="48" height="48"
                           viewBox="0 0 48 48" fill="none">
                          <path
                            d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z"
                            stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                            strokeLinejoin="round"></path>
                          <path d="M4 24H44" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                                strokeLinejoin="round"></path>
                          <path
                            d="M24 4C29.0026 9.47671 31.8455 16.5841 32 24C31.8455 31.4159 29.0026 38.5233 24 44C18.9974 38.5233 16.1545 31.4159 16 24C16.1545 16.5841 18.9974 9.47671 24 4V4Z"
                            stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                            strokeLinejoin="round"></path>
                      </svg>
                      {currentLanguageLabel}
                      <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-900" aria-hidden="true"/>
                  </Menu.Button>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                  <Menu.Items
                    className="absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                          {availableLanguages.map((langCode) => (
                            <Menu.Item key={langCode}>
                                {({active}) => (
                                  <button
                                    onClick={() => changeLanguage(langCode)}
                                    disabled={langCode === language}
                                    className={joinClassNames(
                                      active ? 'bg-gray-100' : '',
                                      langCode === language ? 'font-bold text-gray-900 cursor-default' : 'font-normal text-gray-700',
                                      'w-full text-left block px-4 py-2 text-sm hover:bg-gray-100 no-underline'
                                    )}
                                  >
                                      {languageLabels[langCode] || langCode.toUpperCase()}
                                  </button>
                                )}
                            </Menu.Item>
                          ))}
                      </div>
                  </Menu.Items>
              </Transition>
          </Menu>
        );
    };

    return (
      <header id="header-navigation"
              className="sticky z-50 top-0 w-full -translate-y-0 transition-transform duration-300 delay-0 ease-in border-b border-solid non-show-shadow">
          <div className="h-full w-full z-0">
                <div className="flex w-full bg-leaderfy-300">
                    <div className="w-full mx-auto my-0 max-w-screen-xl flex w-max-lg justify-end">
                        <div className="relative flex flex-row items-center justify-end">
                            <div className="relative rounded-lg h-full flex cursor-pointer py-1.5">
                                <LanguageSwitcher/>
                                <div
                                    className="absolute top-10 -right-px rounded shadow-lg hidden flex-col bg-white min-w-[220px] duration-200 ease-linear delay-0 z-50">
                                    <div>
                                        <span
                                            className="pt-4 pl-4 pb-4 text-base font-normal leading-none relative flex justify-between items-center">English</span>
                                        <span
                                            className="pt-4 pl-4 pb-4 text-base font-normal leading-none relative flex justify-between items-center">Français</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
              <nav className="mx-auto flex w-full items-center justify-between p-2 lg:px-8 bg-white" aria-label="Global">
                  <div className="flex lg:flex-1 items-center pb-0 m-0">
                      <Link to={'/' + language} className="p-1.5 flex items-center">
                          <img className="h-14 w-44 m-0 min-w-[100px] min-h-[40px] md:min-w-[150px] md:min-h-[50px]"
                               alt="Leaderfy Logo"
                               src={logo} />
                      </Link>
                  </div>
                  <div className="flex lg:hidden">
                      <button
                        type="button"
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                        onClick={() => setMobileMenuOpen(true)}
                      >
                          <span className="sr-only">Open main menu</span>
                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" strokeWidth="1.5"
                               stroke="currentColor"
                               aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round"
                                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/>
                          </svg>
                      </button>
                  </div>
                  <Popover.Group className="hidden lg:flex lg:flex-nowrap lg:gap-x-6">
                      {navigation.Items.map((item, index) => {
                            if (item.Children != null && item.Children.length > 0) {
                                const hasActiveChild = item.Children.some(subItem => {
                                    const to = subItem.Link?.Page?.Slug === '/'
                                      ? `/${language}`
                                      : `/${language}/${subItem.Link?.Page?.Slug}`;
                                    return location.pathname.startsWith(to);
                                });

                                return (
                                  <Popover className="relative" key={`popover_${index}`}>
                                      <Popover.Button
                                        className={joinClassNames(
                                          'flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 transition-colors hover:text-leaderfy-500',
                                          hasActiveChild ? 'text-leaderfy-500' : ''
                                        )}>
                                          {item.Name}
                                          <svg className="h-5 w-5 flex-none text-gray-400" aria-hidden="true"
                                               fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                                               stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round"
                                                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                          </svg>
                                      </Popover.Button>

                                      <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-200"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-150"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                      >
                                          <Popover.Panel
                                            className="absolute top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
                                              <div className="p-4">
                                                  {item.Children.map((subItem) => (
                                                    <div
                                                      key={subItem.Name}
                                                      className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm leading-6 hover:bg-gray-50"
                                                    >
                                                        <div className="flex-auto">
                                                            <Link to={
                                                                subItem.Link?.Page?.Slug === '/'
                                                                  ? `/${language}`
                                                                  : `/${language}/${subItem.Link?.Page?.Slug}`
                                                            }
                                                                  className="block font-semibold text-gray-900 no-underline"
                                                                  activeClassName="text-leaderfy-500">{subItem.Name}</Link>
                                                        </div>
                                                    </div>
                                                  ))}
                                              </div>
                                          </Popover.Panel>
                                      </Transition>
                                  </Popover>
                                )
                            }
                            return (
                              <Link to={
                                  item.Link?.Page?.Slug === '/'
                                    ? `/${language}`
                                    : `/${language}/${item.Link?.Page?.Slug}`
                              } className="text-sm font-semibold leading-6 text-gray-900 no-underline"
                                    activeClassName="text-leaderfy-500"
                                    key={item.Name}>
                                  {item.Name}
                              </Link>
                            );
                        }
                      )}
                  </Popover.Group>
                  <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-3">

                      <div className="skip">
                          <div className="w-full h-full flex items-center">
                              <a href="https://app.leaderfy.io/login"
                                 aria-label="Login"
                                 className="cursor-pointer no-underline text-white transition-all ease-linear delay-0 relative h-fit border bg-leaderfy-500 hover:bg-leaderfy-400 rounded-lg py-2 px-4 flex items-center justify-center">
                                  <span className="sc-xwo3p8-0 dqBvni">
                                      <span className="sc-1vtfo9a-0 kdUgic">Login</span>
                                  </span>
                              </a>
                          </div>
                      </div>

                      <div className="skip">
                          <div className="w-full h-full flex items-center">
                              <Popover className="relative">
                                  {({ open }) => (
                                    <>
                                        <Popover.Button
                                          className="cursor-pointer no-underline text-white transition-all ease-linear delay-0 relative h-fit border bg-leaderfy-500 hover:bg-leaderfy-400 rounded-lg py-2 px-4 flex items-center justify-center"
                                        >
                                            <span className="sc-xwo3p8-0 dqBvni">
                                              <span className="sc-1vtfo9a-0 kdUgic">Demo</span>
                                            </span>
                                        </Popover.Button>
                                        <Popover.Panel className="absolute  left-0 z-10 w-96 p-4 mt-2 transform -translate-x-3/4 bg-white border rounded-lg shadow-lg">
                                            <p className="text-sm text-gray-700">
                                                Möchten Sie unser Leaderfy Game ausprobieren? Registrieren Sie sich für eine&nbsp;
                                                <Link to="https://app.leaderfy.io/register" className="text-leaderfy-500 no-underline font-semibold">Demo</Link>.
                                            </p>
                                            <p className="text-sm text-gray-700">
                                                Wenn Sie erfahren möchten, wie das Leaderfy Game in der Personalentwicklung eingesetzt wird,
                                                laden wir Sie zu unserer&nbsp;
                                                <Link to={'/' + language + '/learning-journey'} className="text-leaderfy-500 no-underline font-semibold">Learning Journey</Link> an.
                                                Neben dem Erlebnis des Leaderfy Games bekommen Sie hier auch Einblicke in Auswertungsmöglichkeiten und konkrete Einsatzkonzepte.
                                            </p>
                                        </Popover.Panel>
                                    </>
                                  )}
                              </Popover>
                          </div>
                      </div>

                  </div>
              </nav>
          </div>
          <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
              <div className="fixed inset-0"/>
              <Dialog.Panel
                className="fixed z-99 inset-y-0 right-0 top-24 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                  <div className="mt-6 flow-root">
                      <div className="-my-6 divide-y divide-gray-500/10">
                          <div className="space-y-2 py-6">
                              {navigation.Items.map((item) => {
                                  if (item.Children != null && item.Children.length > 0) {
                                      const hasActiveChild = item.Children.some(subItem => {
                                          const to = subItem.Link?.Page?.Slug === '/'
                                            ? `/${language}`
                                            : `/${language}/${subItem.Link?.Page?.Slug}`;
                                          return location.pathname.startsWith(to);
                                      });
                                      return (
                                        <Disclosure as="div" className="-mx-3" key={item.Name}>
                                            {({open}) => (
                                              <>
                                                  <Disclosure.Button
                                                    className={joinClassNames(
                                                      'flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100',
                                                      hasActiveChild ? 'text-leaderfy-500' : ''
                                                    )}>
                                                      {item.Name}
                                                      <svg
                                                        className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                                                        fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                                                        stroke="currentColor" aria-hidden="true">
                                                          <path strokeLinecap="round" strokeLinejoin="round"
                                                                d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                                      </svg>
                                                  </Disclosure.Button>
                                                  <Disclosure.Panel className="mt-2 space-y-2">
                                                      {item.Children.map((subItem) => (
                                                        <Link
                                                          key={subItem.Name}
                                                          to={subItem.Link?.Page?.Slug === '/'
                                                            ? `/${language}`
                                                            : `/${language}/${subItem.Link?.Page?.Slug}`
                                                          }
                                                          className="block rounded-lg py-2 pl-6 pr-3 font-semibold leading-7 text-gray-900 hover:bg-gray-100 no-underline"
                                                          activeClassName="bg-gray-100 text-leaderfy-500"
                                                        >
                                                            {subItem.Name}
                                                        </Link>
                                                      ))}
                                                  </Disclosure.Panel>
                                              </>
                                            )}
                                        </Disclosure>
                                      )
                                  }
                                  return (
                                    <Link to={
                                        item.Link?.Page?.Slug === '/'
                                          ? `/${language}`
                                          : `/${language}/${item.Link?.Page?.Slug}`
                                    }
                                          key={item.Name}
                                          className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100 no-underline"
                                          activeClassName="bg-gray-100 text-leaderfy-500">
                                        {item.Name}
                                    </Link>
                                  )
                              })}
                          </div>
                      </div>
                  </div>

                  <div className="grid grid-cols-1 divide-x divide-gray-900/5">
                      <a href="https://app.leaderfy.io/login"
                         aria-label="Login"
                         className="w-full cursor-pointer no-underline text-white transition-all ease-linear delay-0 relative h-fit border bg-leaderfy-500 hover:bg-leaderfy-400 rounded-lg py-2 px-4 flex items-center justify-center">
                                  <span className="sc-xwo3p8-0 dqBvni">
                                      <span className="sc-1vtfo9a-0 kdUgic">Login</span>
                                  </span>
                      </a>
                      <Popover className="relative">
                          {({ open }) => (
                            <>
                                <Popover.Button
                                  className="w-full cursor-pointer no-underline text-white transition-all ease-linear delay-0 relative h-fit border bg-leaderfy-500 hover:bg-leaderfy-400 rounded-lg py-2 px-4 flex items-center justify-center"
                                >
                                    <span className="sc-xwo3p8-0 dqBvni">
                                      <span className="sc-1vtfo9a-0 kdUgic">Demo</span>
                                    </span>
                                </Popover.Button>
                                <Popover.Panel className="absolute z-10 w-72 p-4 mt-2 transform -translate-x-1/2 bg-white border rounded-lg shadow-lg left-1/2">
                                    <p className="text-sm text-gray-700">
                                        Möchten Sie unser Leaderfy Game ausprobieren? Registrieren Sie sich für eine&nbsp;
                                        <Link to="https://app.leaderfy.io/register" className="text-leaderfy-500 no-underline font-semibold">Demo</Link>.
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        Wenn Sie erfahren möchten, wie das Leaderfy Game in der Personalentwicklung eingesetzt wird,
                                        laden wir Sie zu unserer&nbsp;
                                        <Link to={'/' + language + '/learning-journey'} className="text-leaderfy-500 no-underline font-semibold">Learning Journey</Link> an.
                                        Neben dem Erlebnis des Leaderfy Games bekommen Sie hier auch Einblicke in Auswertungsmöglichkeiten und konkrete Einsatzkonzepte.
                                    </p>
                                </Popover.Panel>
                            </>
                          )}
                      </Popover>
                  </div>
              </Dialog.Panel>

          </Dialog>
      </header>
    )
}

export default Header
